/**
 * @file smoothScroll.ts
 * @description Utilidades para scroll suave con offset para compensar navbar fijo
 */

/**
 * Realiza scroll suave a un elemento con offset para navbar
 * @param elementId - ID del elemento al que hacer scroll
 * @param offset - Offset adicional en píxeles (por defecto 80px para navbar + padding)
 */
export const scrollToElement = (elementId: string, offset: number = 80): void => {
  const element = document.getElementById(elementId);
  if (!element) {
    console.warn(`Elemento con ID "${elementId}" no encontrado`);
    return;
  }

  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - offset;

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth'
  });
};

/**
 * Realiza scroll suave a la parte superior de la página
 * @param offset - Offset en píxeles (por defecto 0)
 */
export const scrollToTop = (offset: number = 0): void => {
  window.scrollTo({
    top: offset,
    behavior: 'smooth'
  });
};

/**
 * Hook personalizado para manejar scroll con offset
 * @param offset - Offset para compensar navbar fijo
 */
export const useScrollWithOffset = (offset: number = 80) => {
  const scrollTo = (elementId: string) => {
    scrollToElement(elementId, offset);
  };

  const scrollToTopWithOffset = () => {
    scrollToTop(offset);
  };

  return {
    scrollTo,
    scrollToTop: scrollToTopWithOffset
  };
};

/**
 * Configuración automática de enlaces internos con scroll offset
 * Debe llamarse después de que el DOM esté cargado
 */
export const setupSmoothScrollLinks = (offset: number = 80): void => {
  // Buscar todos los enlaces que apuntan a elementos internos
  const internalLinks = document.querySelectorAll('a[href^="#"]');
  
  internalLinks.forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const href = link.getAttribute('href');
      if (href && href.length > 1) {
        const targetId = href.substring(1); // Remover el #
        scrollToElement(targetId, offset);
        
        // Actualizar URL sin hacer scroll
        if (window.history && window.history.pushState) {
          window.history.pushState(null, '', href);
        }
      }
    });
  });
};

/**
 * Hook para detectar la sección activa durante el scroll
 * Útil para highlighting en navegación
 */
import { useState, useEffect } from 'react';

export const useActiveSection = (sectionIds: string[], offset: number = 80) => {
  const [activeSection, setActiveSection] = useState<string>('');

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + offset + 50; // +50 para activar un poco antes

      for (let i = sectionIds.length - 1; i >= 0; i--) {
        const section = document.getElementById(sectionIds[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sectionIds[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Llamada inicial

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sectionIds, offset]);

  return activeSection;
};
